// Shared audio for the SCRUM Nebula. Main bundle on purpose and tiny: the
// AudioContext must be created inside a user gesture (the Konami keydown), so
// the launcher owns it and the lazy game chunk reuses it. One per page, never
// closed (browsers cap live contexts).
const KEY = "massif_scrum_nebula_muted";
let ctx: AudioContext | null = null;
const listeners = new Set<() => void>();

export function loadMuted(
  read: () => string | null = () => localStorage.getItem(KEY)
) {
  try {
    return read() === "1";
  } catch {
    return false; // Storage can be unavailable in private mode or on the server.
  }
}
let muted = loadMuted();

export const isMuted = () => muted;

export function setMuted(next: boolean) {
  muted = next;
  // A sound control can be the first gesture after the ?konami shortcut.
  if (!next && ctx) unlockAudio();
  try {
    localStorage.setItem(KEY, next ? "1" : "0");
  } catch {
    // Storage can be unavailable; keep the choice for this session.
  }
  listeners.forEach((l) => l());
}

export function subscribeMuted(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export type MusicTrack = "obsidian" | "nebula";
const MUSIC_KEY = "massif_scrum_nebula_music";
const musicListeners = new Set<() => void>();
export function loadMusicTrack(
  read: () => string | null = () => localStorage.getItem(MUSIC_KEY)
): MusicTrack {
  try {
    return read() === "nebula" ? "nebula" : "obsidian";
  } catch {
    return "obsidian";
  }
}
let musicTrack = loadMusicTrack();
export const getMusicTrack = () => musicTrack;
export function setMusicTrack(next: MusicTrack) {
  if (ctx) unlockAudio();
  if (next === musicTrack) return;
  musicTrack = next;
  try {
    localStorage.setItem(MUSIC_KEY, next);
  } catch {
    // Keep the selection for this session when storage is unavailable.
  }
  musicListeners.forEach((listener) => listener());
}
export function subscribeMusicTrack(listener: () => void) {
  musicListeners.add(listener);
  return () => {
    musicListeners.delete(listener);
  };
}

// Creates the context on first call, resumes it after; call from a gesture.
export function unlockAudio() {
  if (typeof AudioContext === "undefined") return null;
  try {
    ctx ??= new AudioContext();
    if (ctx.state === "suspended" && !document.hidden)
      void ctx.resume().catch(() => {});
    return ctx;
  } catch {
    return null;
  }
}

export const audioContext = () => ctx;

// The boot overlay's recorded disk seek, loaded only after opening the game.
export function bootChirp(c: AudioContext) {
  let alive = true;
  let stopRecording: (() => void) | undefined;
  const stop = () => {
    alive = false;
    stopRecording?.();
    off();
  };
  const off = subscribeMuted(() => {
    if (muted) stop();
  });
  if (!muted && !document.hidden) {
    const resume = c.state === "running" ? Promise.resolve() : c.resume();
    void Promise.all([resume, import("./game/recordings")])
      .then(async ([, recordings]) => {
        if (!alive) return;
        const buffer = await recordings.loadRecording(c, recordings.BOOT_URL);
        if (!alive || muted || document.hidden || c.state !== "running") return;
        stopRecording = recordings.playRecording(c, c.destination, buffer, 0.1);
      })
      .catch(() => {});
  }
  return stop;
}
